<template>
  <div ref="tabsRoot" class="video-owner-tabs" data-testid="video-owner-tabs">
    <Tabs
      id="video-owner-tabs"
      :selected-tab="selectedTab"
      @tab-click="tab => onTabClick(tab as VideoOwnerTab)"
      @changed="(e: any) => onTabChange(e.tab.hash as VideoOwnerTab)"
    >
      <!-- Summary Tab -->
      <Tab id="summary" :hidden="hideSummary" name="Summary" class="video-owner-tabs__tab">
        <div class="video-owner-tabs__summary">
          <div v-if="description" class="video-owner-tabs__summary-title-row" data-testid="summary-card-title-row">
            <h2 class="video-owner-tabs__summary-title" data-testid="summary-card-title">{{ summaryTitle }}</h2>
            <div v-if="!summaryContentRef?.editMode" class="video-owner-tabs__summary-title-actions">
              <Button
                type="link"
                variant="gray"
                icon="general.edited"
                tooltip="Edit summary"
                aria-label="Edit summary"
                data-testid="summary-edit-btn"
                @click="summaryContentRef?.enterEditMode()"
              />
              <Feedback
                v-if="showFeedback"
                class="video-owner-tabs__feedback"
                :feedback="feedbackState"
                :aiProvider="descriptionProvider"
                :additionalTrackingProps="isSmartActionDescription ? { smart_action_type: smartActionType } : {}"
                @feedback="handleFeedback"
              />
            </div>
          </div>
          <div
            ref="summaryScrollRegion"
            class="video-owner-tabs__summary-body"
            role="region"
            aria-label="Summary"
            tabindex="0"
            data-testid="summary-scroll-region"
            @scroll.passive="updateSummaryOverflowCue"
          >
            <SummaryContent ref="summaryContentRef" />
            <div v-if="hasMoreSummaryBelow" class="video-owner-tabs__summary-overflow-cue" aria-hidden="true" data-testid="summary-overflow-cue" />
          </div>
        </div>
      </Tab>

      <Tab id="to-do" :hidden="!toDoTabEnabled" :name="toDoTabLabel" class="video-owner-tabs__tab">
        <div class="video-owner-tabs__to-do">
          <ToDoContent />
        </div>
      </Tab>

      <!-- Comments & Reactions Tab -->
      <Tab id="comments" :hidden="!engagementTabVisible" :name="engagementTabName" class="video-owner-tabs__tab">
        <div class="video-owner-tabs__engagement">
          <div v-if="!reactionsDisabled" class="video-owner-tabs__reactions-bar" :class="{ 'video-owner-tabs__reactions-bar--bordered': commentsEnabled }">
            <span class="reactions-label">Reactions</span>
            <Reactions :id="id" />
          </div>
          <Comments v-if="commentsEnabled" @addedComment="emit('addedComment')" />
        </div>
      </Tab>

      <!-- Theater Panel Tab -->
      <Tab v-if="theaterPanel" id="theater" :name="theaterPanelLabel" class="video-owner-tabs__tab">
        <component :is="theaterPanel" :key="id" />
      </Tab>

      <!-- Transcript Tab -->
      <Tab id="transcript" :hidden="!transcriptionTabEnabled" name="Transcript" class="video-owner-tabs__tab">
        <Transcription />
      </Tab>

      <!-- Analytics Tab -->
      <Tab id="analytics" :hidden="!analyticsTabEnabled" name="Analytics" class="video-owner-tabs__tab">
        <Analytics />
      </Tab>
    </Tabs>

    <!-- Shares the tab-label row (see the grid styles below); only while the Summary tab is showing a summary. -->
    <div v-if="showRowActions" class="video-owner-tabs__row-actions" data-testid="summary-row-actions">
      <SendToAgentButton />
      <Button
        class="video-owner-tabs__export-btn"
        type="solid"
        variant="blue"
        icon="general.generalExport"
        text="Export summary"
        aria-label="Export summary"
        icon-only-on-mobile
        data-testid="summary-export-btn"
        @click="openExportModal"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
type VideoOwnerTab = '#summary' | '#to-do' | '#comments' | '#theater' | '#transcript' | '#analytics';

import { ref, computed, onBeforeUnmount, onMounted, watch, type Component } from 'vue';
import { storeToRefs } from 'pinia';
import { Tab, Tabs, Button } from '@/storybook';

import Comments from '@/modules/viewer/components/item_tabs/comments/Comments.vue';
import Transcription from '@/modules/viewer/components/item_tabs/transcription/Transcription.vue';
import Analytics from '@/modules/viewer/components/item_tabs/analytics/Analytics.vue';
import SummaryContent from '@/modules/viewer/components/SummaryContent.vue';
import Reactions from '@/modules/viewer/components/Reactions.vue';
import Feedback from '@/storybook/templates/smart_actions/Feedback.vue';
import ToDoContent from '@/modules/viewer/components/ToDoContent.vue';
import SendToAgentButton from '@/modules/viewer/components/SendToAgentButton.vue';

import { useViewerItemStore, ItemType } from '@/modules/viewer/store/useViewerItemStore';
import { useSmartActionsStore } from '@/modules/viewer/store/useSmartActionsStore';
import { useTranscriptionStore, ItemTranscriptionStatus } from '@/modules/viewer/store/useTranscriptionStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useUser } from '@/modules/viewer/composables/useUser';
import { useItemActions } from '@/modules/viewer/composables/useItemActions';
import { useTabNavigation } from '@/modules/viewer/composables/useTabNavigation';
import { useActiveTabListener } from '@/modules/viewer/composables/useActiveTab';
import { useEngagementTab } from '@/modules/viewer/composables/useEngagementTab';
import { useSummaryFeedback } from '@/modules/viewer/composables/useSummaryFeedback';
import { useVideoAi } from '@/modules/viewer/composables/useVideoAi';
import { useToDo } from '@/modules/viewer/composables/useToDo';
import { useGuideNavigationListener } from '@/modules/viewer/composables/useGuideNavigation';

const props = withDefaults(
  defineProps<{
    hideSummary?: boolean;
    theaterPanel?: Component | null;
    theaterPanelLabel?: string;
    showToDo?: boolean;
  }>(),
  { hideSummary: false, theaterPanel: null, theaterPanelLabel: '', showToDo: false }
);

const emit = defineEmits<{
  addedComment: [];
}>();

const store = useViewerItemStore();
const transcriptionStore = useTranscriptionStore();
const smartActionsStore = useSmartActionsStore();
const organizationStore = useOrganizationStore();
const { isCurrentItemOwner } = useUser();
const { openShareModalAction } = useItemActions();
const tabsRoot = ref<HTMLElement | null>(null);

const { id, type, commentsEnabled, reactionsDisabled, description } = storeToRefs(store);
const { engagementTabName, engagementTabVisible } = useEngagementTab();
const { transcriptionStatus } = storeToRefs(transcriptionStore);
const { trial } = storeToRefs(organizationStore);

const { descriptionProvider, feedbackState, handleFeedback, isSmartActionDescription, showFeedback, smartActionType } = useSummaryFeedback();

const { hasAiAddon } = useVideoAi();
const { toDoTabLabel, isToDoAvailable } = useToDo({ badgeClass: 'video-owner-tabs__todo-badge' });

const summaryContentRef = ref<InstanceType<typeof SummaryContent> | null>(null);
const summaryScrollRegion = ref<HTMLElement | null>(null);
const hasMoreSummaryBelow = ref(false);

const updateSummaryOverflowCue = () => {
  const region = summaryScrollRegion.value;
  if (!region) {
    hasMoreSummaryBelow.value = false;
    return;
  }
  hasMoreSummaryBelow.value = region.scrollTop + region.clientHeight < region.scrollHeight - 1;
};

let summaryResizeObserver: ResizeObserver | undefined;

onMounted(() => {
  const region = summaryScrollRegion.value;
  if (!region) {
    return;
  }
  summaryResizeObserver = new ResizeObserver(updateSummaryOverflowCue);
  summaryResizeObserver.observe(region);
  if (region.firstElementChild) {
    summaryResizeObserver.observe(region.firstElementChild);
  }
  updateSummaryOverflowCue();
});

onBeforeUnmount(() => {
  summaryResizeObserver?.disconnect();
});

const toDoTabEnabled = computed(() => props.showToDo && hasAiAddon.value && isToDoAvailable.value);

const transcriptionTabEnabled = computed(() => {
  return (
    type.value === ItemType.VIDEO &&
    (isCurrentItemOwner.value ||
      (transcriptionStatus.value && [ItemTranscriptionStatus.DONE, ItemTranscriptionStatus.EDITED].includes(transcriptionStatus.value)))
  );
});

const analyticsTabEnabled = computed(() => {
  return isCurrentItemOwner.value && (trial?.value === undefined || trial?.value === false);
});

const openExportModal = () => {
  openShareModalAction({ modalProps: { selectedTab: '#export' } });
};

const availableTabs = computed(() => {
  const tabs: VideoOwnerTab[] = [];
  if (!props.hideSummary) {
    tabs.push('#summary');
  }
  if (toDoTabEnabled.value) {
    tabs.push('#to-do');
  }
  if (engagementTabVisible.value) {
    tabs.push('#comments');
  }
  if (props.theaterPanel) {
    tabs.push('#theater');
  }
  if (transcriptionTabEnabled.value) {
    tabs.push('#transcript');
  }
  if (analyticsTabEnabled.value) {
    tabs.push('#analytics');
  }
  return tabs;
});

const { selectedTab, onTabClick, onTabChange, selectTab } = useTabNavigation(availableTabs, tabsRoot, '#summary');

const summaryTitle = computed(() => {
  if (!isSmartActionDescription.value) {
    return 'Summary';
  }
  const subcategory = smartActionsStore.categories?.flatMap(category => category.subcategories).find(({ key }) => key === smartActionType.value);
  return subcategory?.title ?? 'Summary';
});

const showRowActions = computed(() => selectedTab.value === '#summary' && !!description.value && !summaryContentRef.value?.editMode);

// Listen for external tab switch requests (from ButtonViews, EngagementBar, etc.)
const { activeTabOverride } = useActiveTabListener();
watch(activeTabOverride, request => {
  if (request) {
    selectTab(request.tab, { alwaysScroll: request.alwaysScroll });
    activeTabOverride.value = null;
  }
});

// "View Guide" in the Smart Actions panel: the guide lives in the Summary tab below the video.
useGuideNavigationListener(summaryContentRef, () => selectTab('#summary'));
</script>

<style scoped>
.video-owner-tabs {
  @apply mt-4 grid gap-y-3 md:gap-y-2;
  grid-template-columns: minmax(0, 1fr);
}

/* The tabs library wraps its nav and panels in one flex column; dissolving that wrapper
   lets the nav and the row actions become grid items next to the panels. */
.video-owner-tabs :deep(.tabs-component) {
  display: contents;
}

/* Phones: tab labels, then the actions right-aligned on their own line, so no label is clipped. */
.video-owner-tabs :deep(.tabs-component-tabs) {
  order: -2;
}

.video-owner-tabs__row-actions {
  @apply flex items-center justify-end gap-2 self-center pb-1;
  order: -1;
}

/* Wider: the actions share the tab-label row. */
@media (min-width: 640px) {
  .video-owner-tabs {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .video-owner-tabs :deep(.tabs-component-tabs) {
    grid-area: 1 / 1;
  }

  .video-owner-tabs__row-actions {
    grid-area: 1 / 2;
  }

  .video-owner-tabs :deep(.tabs-component-panels) {
    grid-area: 2 / 1 / 3 / -1;
  }
}

.video-owner-tabs__tab {
  @apply mt-3 sm:mt-4;
  min-height: 200px;
}

@media (min-width: 640px) {
  .video-owner-tabs__tab {
    min-height: 300px;
  }
}

.video-owner-tabs__summary {
  @apply relative flex flex-col p-3 sm:p-4 border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 rounded-10;
}

.video-owner-tabs__summary-body {
  @apply rounded-sm focus:outline-none focus-visible:ring focus-visible:ring-blue-500/50;
}

/* Scrolled to the top of the window, the card fits the viewport with its
   action bar visible and the summary text scrolls inside it. */
@media (min-width: 1024px) {
  .video-owner-tabs__summary-body {
    max-height: max(16rem, calc(100vh - 12rem));
    overflow-y: auto;
    overscroll-behavior: contain;
  }
}

.video-owner-tabs__summary-overflow-cue {
  @apply sticky bottom-0 h-10 -mt-10 pointer-events-none bg-gradient-to-b from-transparent to-white dark:to-zinc-900;
}

.video-owner-tabs__summary-title-row {
  @apply flex items-center justify-between gap-2 mb-2;
}

.video-owner-tabs__summary-title {
  @apply min-w-0 truncate text-base font-semibold text-zinc-900 dark:text-zinc-100;
}

.video-owner-tabs__summary-title-actions {
  @apply flex shrink-0 items-center gap-1;
}

/* Edit and the feedback thumbs as 28px grey icon buttons. */
.video-owner-tabs__summary-title-actions :deep(.button-base.regular) {
  @apply h-7 w-7 p-0 rounded-md text-slate-500 dark:text-zinc-400;
}

.video-owner-tabs__feedback :deep(.feedback-buttons) {
  @apply gap-1;
}

.video-owner-tabs__feedback :deep(.button) {
  --button-block-size: 1.75rem;
  --button-padding: 0;
  @apply h-7 w-7 justify-center rounded-md;
  color: var(--slate-500, #64748b);
}

.dark .video-owner-tabs__feedback :deep(.button) {
  color: var(--zinc-400, #94a6b8);
}

.video-owner-tabs__to-do {
  @apply relative flex flex-col p-3 sm:p-4 border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 rounded-10;
}

/* Matches BaseBadge.vue - can't reuse the component itself since this
   markup is injected via the tabs library's innerHTML, outside Vue's
   render tree. */
:deep(.video-owner-tabs__todo-badge) {
  @apply ml-1 inline-block rounded-full bg-slate-100 px-1.5 text-sm text-slate-500 dark:bg-zinc-600 dark:text-zinc-400;
}

.video-owner-tabs__engagement {
  @apply relative flex flex-col p-3 sm:p-4 gap-3 sm:gap-4 border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 rounded-10;
}

.video-owner-tabs__reactions-bar {
  @apply flex flex-wrap items-center gap-2 sm:gap-3;
}

.video-owner-tabs__reactions-bar--bordered {
  @apply pb-3 border-b border-slate-300 dark:border-zinc-600;
}

.reactions-label {
  @apply text-sm font-medium text-zinc-600 dark:text-zinc-400 mr-1;
}
</style>
